import React, { useState } from "react";
import { Settings2, Star, Share2, EyeOff, Trash2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ResponsivePopover } from "@/components/ui/responsive-popover";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { SavedViewsTabMenu } from "./SavedViewsTabMenu";
import { SavedViewConfigPreview } from "./SavedViewConfigPreview";
import {
  getSavedViewSummary,
  type FieldMap,
} from "../../../UniversalCRMTable/components/SimpleTable/filterDisplayUtils";
import type { SavedViewConfig } from "../../../UniversalCRMTable/types";
import type { TabItem } from "./types";

interface SavedViewTabPillProps {
  tab: TabItem;
  view?: SavedViewConfig;
  fields: FieldMap;
  isActive: boolean;
  isUserCreated: boolean;
  isFavorite: boolean;
  onTabClick: (viewId: string | null) => void;
  onToggleFavorite: (viewId: string) => void;
  onShare: (viewId: string) => void;
  onDelete: (viewId: string) => void;
  onHide: (viewId: string) => void;
}

/** One-line hover summary of a view's filters. */
function buildQuickSummary(view: SavedViewConfig, fields: FieldMap): string {
  const summary = getSavedViewSummary(view, fields);
  if (summary.isEmpty) return "All records — no filters applied.";

  const bits: string[] = [];
  if (summary.search) bits.push(`Search "${summary.search}"`);
  summary.filterEntries.forEach((e) => bits.push(`${e.label}: ${e.parts.summary}`));
  if (summary.advancedCount > 0) {
    bits.push(`${summary.advancedCount} advanced condition${summary.advancedCount === 1 ? "" : "s"}`);
  }
  return bits.join(" • ");
}

export const SavedViewTabPill: React.FC<SavedViewTabPillProps> = ({
  tab,
  view,
  fields,
  isActive,
  isUserCreated,
  isFavorite,
  onTabClick,
  onToggleFavorite,
  onShare,
  onDelete,
  onHide,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const hasConfig = !!view;

  const pillInner = (
    <div
      className={`flex items-center rounded-full font-medium text-2xs whitespace-nowrap transition-all flex-shrink-0 ${
        isActive
          ? "bg-blue-600 text-white shadow-md"
          : "bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600"
      }`}
    >
      <button type="button" onClick={() => onTabClick(tab.id)} className="px-2 py-0.5 flex-1">
        {tab.name}
      </button>

      {isUserCreated && tab.id && (
        <SavedViewsTabMenu
          isFavorite={isFavorite}
          onToggleFavorite={() => onToggleFavorite(tab.id as string)}
          onShare={() => onShare(tab.id as string)}
          onDelete={() => onDelete(tab.id as string)}
        />
      )}
    </div>
  );

  if (!hasConfig || !view) {
    return <div className="relative group flex-shrink-0">{pillInner}</div>;
  }

  const viewId = tab.id;

  const configPreview = (
    <SavedViewConfigPreview
      view={view}
      fields={fields}
      onHide={
        viewId
          ? () => {
              onHide(viewId);
              setPreviewOpen(false);
            }
          : undefined
      }
    />
  );

  // The preview popover is opened from the context menu, never by clicking the
  // pill — so the wrapper below is only its anchor (ResponsivePopover is
  // controlled, and its desktop Mantine target adds no click handler of its own).
  return (
    <ResponsivePopover forcePopover
      open={previewOpen}
      onOpenChange={setPreviewOpen}
      content={configPreview}
      title="View configuration"
      align="start"
      popoverSide="bottom"
      className="w-72 p-0"
      showCloseButton={false}
    >
      <div className="relative group flex-shrink-0">
        <ContextMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <ContextMenuTrigger asChild>{pillInner}</ContextMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[300px]">
              {buildQuickSummary(view, fields)}
              <span className="block mt-1 text-[10px] opacity-70">
                Right-click for actions
              </span>
            </TooltipContent>
          </Tooltip>

          <ContextMenuContent className="w-48">
            <ContextMenuItem onSelect={() => setPreviewOpen(true)}>
              <Settings2 className="mr-2 h-4 w-4" />
              View configuration
            </ContextMenuItem>

            {isUserCreated && viewId && (
              <>
                <ContextMenuSeparator />
                <ContextMenuItem onSelect={() => onToggleFavorite(viewId)}>
                  <Star
                    className="mr-2 h-4 w-4"
                    fill={isFavorite ? "currentColor" : "none"}
                  />
                  {isFavorite ? "Unfavorite" : "Favorite"}
                </ContextMenuItem>
                <ContextMenuItem onSelect={() => onShare(viewId)}>
                  <Share2 className="mr-2 h-4 w-4" />
                  Share
                </ContextMenuItem>
              </>
            )}

            {viewId && (
              <ContextMenuItem onSelect={() => onHide(viewId)}>
                <EyeOff className="mr-2 h-4 w-4" />
                Hide from tabs
              </ContextMenuItem>
            )}

            {isUserCreated && viewId && (
              <>
                <ContextMenuSeparator />
                <ContextMenuItem
                  variant="destructive"
                  onSelect={() => onDelete(viewId)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </ContextMenuItem>
              </>
            )}
          </ContextMenuContent>
        </ContextMenu>
      </div>
    </ResponsivePopover>
  );
};
